import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  MdSearch,
  MdPictureAsPdf,
  MdFolderZip,
  MdPersonSearch,
  MdPersonOff,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_URL } from "../../utils/constants";
import { cssProperties } from "../../utils/commonCssProperties";
import masterActions from "../../redux/master/actions";
import commonActions from "../../redux/common/actions";

/**
 * Admit Card Verification - the Admit Card Verifier role's only screen.
 *
 * Search one candidate by course + Application Number (exact), see who it is,
 * and download the STORED admit card (PDF, or a ZIP holding that one PDF) to
 * check it was generated correctly. The backend records nothing about these
 * searches or downloads - see controllers/backoffice/admitCardVerificationController.js.
 */

const API = `${API_URL}/backoffice/admit-card-verification`;

const BRAND = cssProperties?.color?.primary;
const BUTTON_BG = cssProperties?.buttonbgcolor?.primary;
const BUTTON_FG = cssProperties?.buttonfontcolor?.black;
const MUTED = cssProperties?.color?.textsecondary;
const BORDER = cssProperties?.bordercolor?.secondary;

const panelSx = {
  p: { xs: 2, md: 3 },
  mb: 2.5,
  borderRadius: "16px",
  border: `1px solid ${BORDER}`,
  boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
  backgroundColor: cssProperties?.backgroundcolor?.secondary,
};

const labelSx = { mb: 0.75, fontSize: 13, fontWeight: 500, color: MUTED };

const pillButtonSx = {
  borderRadius: "40px",
  px: 3,
  py: 1.1,
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: "0.32px",
  boxShadow: "none",
  whiteSpace: "nowrap",
};

const primaryButtonSx = {
  ...pillButtonSx,
  backgroundColor: BUTTON_BG,
  color: BUTTON_FG,
  "&:hover": { backgroundColor: BUTTON_BG, boxShadow: "none", filter: "brightness(0.96)" },
};

const outlineButtonSx = {
  ...pillButtonSx,
  border: `1px solid ${BUTTON_BG}`,
  color: BUTTON_FG,
  "&:hover": { border: `1px solid ${BUTTON_BG}`, backgroundColor: "rgba(230, 151, 171, 0.08)" },
};

const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

// With responseType "blob", error bodies arrive as a Blob too - read the JSON
// message back out of it so the user sees the API's reason, not a generic one.
const blobErrorMessage = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.message;
    } catch {
      return null;
    }
  }
  return data?.message;
};

const EmptyState = ({ icon: Icon, title, text }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: cssProperties?.hoverbgcolor?.sidebar,
        color: BRAND,
      }}
    >
      <Icon size={30} />
    </Box>
    <Typography sx={{ fontSize: 16, fontWeight: 600, color: cssProperties?.fontcolor?.primary }}>
      {title}
    </Typography>
    {text && (
      <Typography sx={{ mt: 0.5, fontSize: 14, color: MUTED, maxWidth: 440, mx: "auto" }}>
        {text}
      </Typography>
    )}
  </Box>
);

const Detail = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 12, color: MUTED, mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 600, wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

export default function AdmitCardVerifier() {
  const dispatch = useDispatch();
  const { courseList } = useSelector((state) => state.masterReducer);

  const [courseId, setCourseId] = useState(0);
  const [applicationNo, setApplicationNo] = useState("");
  const [candidate, setCandidate] = useState(null);
  const [notFound, setNotFound] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    dispatch({ type: masterActions.GET_COURSE_LIST });
  }, [dispatch]);

  // The shared toast never closes itself - the screen that opens it must hide
  // it, as the other screens do after 2s. The timer is kept so a newer alert
  // is not cut short by an older one's timeout.
  const alertTimer = useRef(null);

  const hideAlert = () => {
    clearTimeout(alertTimer.current);
    dispatch({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  };

  const showError = (message) => {
    clearTimeout(alertTimer.current);
    dispatch({
      type: commonActions.SET_ALERT,
      payload: { show: true, status: "failed", message },
    });
    alertTimer.current = setTimeout(hideAlert, 2000);
  };

  // Leaving the page must not strand an open alert on the next screen.
  useEffect(
    () => () => {
      clearTimeout(alertTimer.current);
      dispatch({
        type: commonActions.SET_ALERT,
        payload: { status: null, show: false, message: null },
      });
    },
    [dispatch]
  );

  const resetResults = () => {
    setCandidate(null);
    setNotFound(null);
  };

  const handleSearch = async () => {
    const appNo = applicationNo.trim();

    if (!courseId || !appNo) {
      showError("Select a course and enter an Application Number");
      return;
    }

    hideAlert();
    resetResults();
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/search?courseid=${courseId}&applicationno=${encodeURIComponent(appNo)}`
      );
      setCandidate(response?.data?.result || null);
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to search the candidate";
      // "No such application number" stays on the page (the toast fades);
      // anything else is a real failure and only gets the toast.
      if (error?.response?.status === 404) setNotFound(message);
      else showError(message);
    } finally {
      setLoading(false);
    }
  };

  // Uses the application number the candidate panel shows, not the input box,
  // so editing the box after a search cannot fetch someone else's card.
  const handleDownload = async (format) => {
    if (!candidate) return;

    setDownloading(format);
    try {
      const response = await axios.get(
        `${API}/download?courseid=${courseId}&applicationno=${encodeURIComponent(
          candidate.applicationnumber
        )}&format=${format}`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${candidate.applicationnumber}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError((await blobErrorMessage(error)) || "Failed to download the admit card");
    } finally {
      setDownloading(null);
    }
  };

  const spinner = <CircularProgress size={16} sx={{ color: BUTTON_FG }} />;

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      {/* Search */}
      <Paper sx={panelSx}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 0.5 }}>Search Candidate</Typography>
        <Typography sx={{ fontSize: 14, color: MUTED, mb: 2.5 }}>
          Select the course and enter the candidate's Application Number.
        </Typography>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={5}>
            <Typography sx={labelSx}>Course</Typography>
            <Select
              fullWidth
              size="small"
              value={courseId}
              displayEmpty
              sx={{ borderRadius: "10px", backgroundColor: "#fff" }}
              onChange={(e) => {
                // A result belongs to the course it was searched under; clearing
                // it keeps the download from pairing this candidate with another
                // course.
                setCourseId(e.target.value);
                resetResults();
              }}
            >
              <MenuItem value={0} disabled>
                <Typography component="span" sx={{ color: MUTED }}>
                  Select a course
                </Typography>
              </MenuItem>
              {courseList?.all?.map((course, i) => (
                <MenuItem key={i} value={course.courseid} sx={{ whiteSpace: "nowrap" }}>
                  {course.coursename}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography sx={labelSx}>Application No.</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Application Number"
              value={applicationNo}
              onChange={(e) => setApplicationNo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              inputProps={{ inputMode: "numeric", autoComplete: "off" }}
              InputProps={{
                sx: { borderRadius: "10px", backgroundColor: "#fff" },
                startAdornment: (
                  <InputAdornment position="start">
                    <MdSearch size={20} color={MUTED} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? spinner : <MdSearch />}
              sx={primaryButtonSx}
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Before any search */}
      {!loading && !candidate && !notFound && (
        <Paper sx={panelSx}>
          <EmptyState
            icon={MdPersonSearch}
            title="Find a candidate"
            text="Search by Application Number to view the candidate and download their admit card."
          />
        </Paper>
      )}

      {/* Searched, nothing matched */}
      {!loading && notFound && (
        <Paper sx={panelSx}>
          <EmptyState icon={MdPersonOff} title="No candidate found" text={notFound} />
        </Paper>
      )}

      {/* Candidate */}
      {!loading && candidate && (
        <Paper sx={panelSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Avatar
              sx={{
                width: 52,
                height: 52,
                fontSize: 18,
                fontWeight: 600,
                backgroundColor: cssProperties?.hoverbgcolor?.sidebar,
                color: BRAND,
              }}
            >
              {initials(candidate.candidatename)}
            </Avatar>
            <Typography sx={{ flex: 1, minWidth: 200, fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>
              {candidate.candidatename || "-"}
            </Typography>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Detail label="Application No." value={candidate.applicationnumber} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Detail label="Roll No." value={candidate.rollno} />
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Same value the admit card prints as "Name of the Course" -
                  the candidate's exam, not the course picked in the dropdown. */}
              <Detail
                label="Course"
                value={candidate.examcoursename || candidate.coursename}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            {candidate.admitCardAvailable ? (
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  disableElevation
                  disabled={Boolean(downloading)}
                  onClick={() => handleDownload("pdf")}
                  startIcon={downloading === "pdf" ? spinner : <MdPictureAsPdf />}
                  sx={primaryButtonSx}
                >
                  {downloading === "pdf" ? "Downloading..." : "Download Admit Card (PDF)"}
                </Button>
                <Button
                  variant="outlined"
                  disabled={Boolean(downloading)}
                  onClick={() => handleDownload("zip")}
                  startIcon={downloading === "zip" ? spinner : <MdFolderZip />}
                  sx={outlineButtonSx}
                >
                  {downloading === "zip" ? "Downloading..." : "Download Admit Card (ZIP)"}
                </Button>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: "10px" }}>
                {candidate.unavailableReason || "Admit card is not available for this candidate"}
              </Alert>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
